import React, { FunctionComponent, useMemo, useState } from 'react'
import { Box } from '@mui/material'
import { WithTreeIds } from './navigationHelpers/WithTree'
import { AltText } from './altText/AltText'
import { copyToClipboard } from 'shared/utils'
import { TreeManagementToolSelect } from 'shared/ui'
import { ImageSources } from './explore/ImageSources'
import { CopyAndShare } from './CopyAndShare'
import { useGetTreeById } from 'shared/hooks/trees/hooks'
import DeleteLeaves from './DeleteLeaves'
import { Summaries } from './summaries/Summaries'
import { TreeUrlSwap } from '../tree_fixes/PresentationUrlSwap/SwapUI'
import { FindNodeTypes } from './explore/FindNodeTypes'
import { Builder } from '../Builder'
import { ContentNumbering } from './contentNumbering/ContentNumbering'
import { FindAndReplace } from './findAndReplace/FindAndReplace'
import { ReplaceStandards } from './replaceStandards/ReplaceStandards'
import { GDriveTables } from './gDriveTables'
import { NodeSearchParams, openNode } from '../utility/TreeNavigation'
import { UPStandards } from './UPStandards/Index'
import { BulkAddContent } from './BulkAddContent/BulkAddContent'
import { BulkAddStandards } from './BulkAddStandards/BulkAddStandards'
import { StandardsCascadeTool } from './StandardsCascadeTool/StandardsCascadeTool'
import { NodeReorderWrapper } from './NodeReorder'
import { TreeManipulation } from './treeManipulation/TreeManipulation'
import { TreeEditingV2 } from './TreeEditingV2/TreeEditingV2'
import { NodeReplace } from './NodeReplace/NodeReplace'
import { NodeCrossAssociation } from './nodeCrossAssociation/NodeCrossAssociation'
import { SVGtoLatexWrapper } from './svgToLaTEXConverter/svgToLaTEXConverter'
import { useClearTreeMgmtTree } from './hooks'
import { FilledInButton, OutlinedButton } from 'shared/ui'
import Typography from '@mui/material/Typography'
import { lastEditedDate } from '../utility/TreeHelpers'
import { BulkAddStandardsV2 } from './BulkAddStandardsV2/BulkAddStandardsV2'
import { TreeVerifications } from './TreeVerifications/TreeVerifications'
import { AITreeTool } from './AITreeTool/AITreeTool'
import { UpdatePresentationsSlides } from './UpdatePresentationsSlides/UpdatePresentationsSlides'
import { SyncStandards } from './SyncStandards/SyncStandards'
import { NodeTaggingWrapper } from './NodeTagging'
import { SlidesGenerator } from './SlidesGenerator/SlidesGenerator'
import { AssetUpload } from './AssetUpload/AssetUpload'

export function TreeManagementWrapper() {
  return <WithTreeIds Wrapped={TreeManagementWithTitle} />
}

export function TreeManagementWithTitle(ids: {
  treeId: string
  versionId: string
  nodeId?: string
}) {
  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <TreeTitle {...ids} />
        <ButtonGroup {...ids} />
      </Box>
      <TreeManagement {...ids} />
    </Box>
  )
}

export function TreeManagement(ids: { treeId: string; versionId: string }) {
  const componentOptions = useTreeTools(ids)
  return (
    <Box padding="20px 10px">
      <TreeManagementToolSelect components={componentOptions} />
    </Box>
  )
}

const treeTools: {
  title: string
  Component: FunctionComponent<{ treeId: string; versionId: string }>
}[] = [
  { title: 'Copy Tree', Component: CopyAndShare },
  { title: 'Alt Text', Component: AltText },
  { title: 'Replace Standards', Component: ReplaceStandards },
  { title: 'Image Sources', Component: ImageSources },
  { title: 'Delete Skill Leaves', Component: DeleteLeaves },
  { title: 'Node Summaries', Component: Summaries },
  { title: 'Presentation Url Swap', Component: TreeUrlSwap },
  {
    title: 'Update Presentations Slides',
    Component: UpdatePresentationsSlides,
  },
  { title: 'Tree Verifications', Component: TreeVerifications },
  { title: 'Tree Explore', Component: FindNodeTypes },
  { title: 'Tree Builder', Component: Builder },
  { title: 'Node Reorder', Component: NodeReorderWrapper },
  { title: 'Content Numbering', Component: ContentNumbering },
  { title: 'Find & Replace', Component: FindAndReplace },
  { title: 'GDrive Table Import', Component: GDriveTables },
  { title: 'UP Standards', Component: UPStandards },
  { title: 'Bulk Add Content', Component: BulkAddContent },
  { title: 'Bulk Add Standards', Component: BulkAddStandards },
  { title: 'Bulk Add Standards V2', Component: BulkAddStandardsV2 },
  { title: 'Sync Standards', Component: SyncStandards },
  { title: 'Standards Cascade Tool', Component: StandardsCascadeTool },
  { title: 'Tree Manipulation', Component: TreeManipulation },
  { title: 'Tree Editing V2', Component: TreeEditingV2 },
  { title: 'Node Replace', Component: NodeReplace },
  { title: 'Node Cross Association', Component: NodeCrossAssociation },
  { title: 'SVG to LaTEX Converter', Component: SVGtoLatexWrapper },
  { title: 'AI Tree Tool', Component: AITreeTool },
  { title: 'Slides Generator', Component: SlidesGenerator },
  { title: 'Asset Upload', Component: AssetUpload },
  { title: 'Node Tagging', Component: NodeTaggingWrapper },
]

function useTreeTools({
  treeId,
  versionId,
}: {
  treeId: string
  versionId: string
}) {
  return useMemo(() => {
    return treeTools
      .map(({ title, Component }) => ({
        title,
        component: () => (
          <Component key={title} treeId={treeId} versionId={versionId} />
        ),
      }))
      .sort((a, b) => a.title.localeCompare(b.title))
  }, [treeId, versionId])
}

function ButtonGroup(ids: {
  treeId: string
  versionId: string
  nodeId?: string
}) {
  return (
    <Box display="flex">
      {[ClearCurriculaButton, CopyIdsButton, OpenKiddomButton].map(
        (ButtonComp, idx) => {
          return (
            <Box key={idx} padding="5px">
              <ButtonComp {...ids} />
            </Box>
          )
        },
      )}
    </Box>
  )
}

function ClearCurriculaButton() {
  const navigate = useClearTreeMgmtTree()
  return (
    <FilledInButton onClick={() => navigate()}>
      Change Curriculum
    </FilledInButton>
  )
}

function CopyIdsButton({
  treeId,
  versionId,
}: {
  treeId: string
  versionId: string
}) {
  const [pressed, setPressed] = useState(false)
  const onPress = () => {
    setPressed(true)
    copyToClipboard(`${treeId}:${versionId}`)
  }
  return (
    <OutlinedButton onClick={onPress}>
      {pressed ? `${treeId}:${versionId}` : 'Copy Ids'}
    </OutlinedButton>
  )
}

function OpenKiddomButton({
  treeId,
  versionId,
}: {
  treeId: string
  versionId: string
}) {
  return (
    <OutlinedButton
      onClick={() =>
        openNode({
          tree_id: treeId,
          version: versionId,
        } as NodeSearchParams)
      }
    >
      Open in Kiddom
    </OutlinedButton>
  )
}

function TreeTitle(ids: {
  treeId: string
  versionId: string
  nodeId?: string
}) {
  const { tree } = useGetTreeById(ids)

  if (!tree) return <Box>Retrieving tree data...</Box>

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold">
        {tree.title}
      </Typography>
      <Typography variant="body2">
        Created: {tree.created_at.split('T')[0]} • Last Edited:{' '}
        {lastEditedDate(tree).split('T')[0]}
      </Typography>
    </Box>
  )
}
